// src/components/molecules/TermsAcceptance.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import Iframe from "../atoms/Iframe"; // Ensure this component can accept onLoad and onError props
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";

const TermsAcceptance = ({ termsUrl, onAccept, onReject, theme }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  return (
    <Paper
      elevation={3}
      sx={{
        height: "400px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #ccc",
        position: "relative", // To position overlays correctly
      }}
    >
      {/* Iframe for Membership Terms */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "scroll",
          p: 2,
          borderBottom: "1px solid #ccc",
          position: "relative", // For loader and error positioning
        }}
      >
        {/* Loader Overlay */}
        {!iframeLoaded && !iframeError && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent background
              zIndex: 1, // Ensure it overlays the iframe
            }}
          >
            <CircularProgress sx={{ color: theme.colors.red }} />
          </Box>
        )}

        {/* Error Overlay */}
        {iframeError && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <Alert severity="error">
              Failed to load terms. Please try again.
            </Alert>
          </Box>
        )}

        {/* Iframe Component */}
        <Iframe
          src={termsUrl}
          title="Membership Terms"
          width="100%"
          height="100%"
          onLoad={() => setIframeLoaded(true)}
          onError={() => setIframeError(true)}
        />
      </Box>

      {/* Accept/Reject Buttons */}
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            onClick={onAccept}
            sx={{
              backgroundColor: theme.colors.green,
              border: "1px solid transparent",
              color: theme.colors.white,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                border: `1px solid ${theme.colors.green}`,
                backgroundColor: theme.colors.white,
                color: theme.colors.green,
                boxShadow: "none",
              },
            }}
            aria-label="Accept Terms and Conditions"
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            onClick={onReject}
            color="error"
            sx={{
              backgroundColor: theme.colors.red,
              color: theme.colors.white,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.white,
                color: theme.colors.red,
                boxShadow: "none",
              },
            }}
            aria-label="Reject Terms and Conditions"
          >
            Reject
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

TermsAcceptance.propTypes = {
  termsUrl: PropTypes.string.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

export default TermsAcceptance;
